import { formatPostingDate } from './glPostingMethods';

/**
 * Group sublocations by their last posted date range
 * @param {Array} sublocations - Array of sublocation objects with lastPosting data
 * @returns {Array} Array of grouped sublocation objects
 */
export const groupSublocationsByLastPosting = (sublocations) => {
  if (!Array.isArray(sublocations) || sublocations.length === 0) {
    return [];
  }

  const groups = new Map();

  sublocations.forEach((sublocation) => {
    let groupKey;
    let label;
    let dateRange = null;

    const lastPosting = sublocation?.lastPosting;
    const hasValidDates = lastPosting?.startDate && lastPosting?.endDate;

    if (!hasValidDates) {
      // No previous posting group
      groupKey = 'no-previous-posting';
      label = 'NO PREVIOUS POSTING';
    } else {
      // Group by date range
      const { startDate, endDate } = lastPosting;
      groupKey = `${startDate}_${endDate}`;
      dateRange = { start: startDate, end: endDate };

      // Format dates for label
      const startFormatted = formatPostingDate(startDate);
      const endFormatted = formatPostingDate(endDate);
      label = `LAST POSTED: ${startFormatted} – ${endFormatted}`;
    }

    if (!groups.has(groupKey)) {
      groups.set(groupKey, {
        id: groupKey,
        label,
        dateRange,
        count: 0,
        sublocations: [],
        color: getGroupColor(groupKey),
      });
    }

    const group = groups.get(groupKey);
    group.sublocations.push(sublocation);
    group.count++;
  });

  // Convert to array and sort
  return Array.from(groups.values()).sort((a, b) => {
    // "No previous posting" always first
    if (!a.dateRange) return -1;
    if (!b.dateRange) return 1;

    // Sort by date (most recent first)
    return new Date(b.dateRange.start).getTime() - new Date(a.dateRange.start).getTime();
  });
};

/**
 * Generate a consistent color for a group based on its key
 * Uses UNIFI brand colors to align with the project's design system
 * @param {string} groupKey - Unique group identifier
 * @returns {string} Hex color code
 */
const getGroupColor = (groupKey) => {
  // UNIFI Brand Colors
  const NO_PREVIOUS_POSTING_COLOR = '#ff0000'; // Red - For "No Previous Posting"
  const DEFAULT_COLOR = '#577188'; // Secondary Blue-Grey - For all other groups

  if (groupKey === 'no-previous-posting') {
    return NO_PREVIOUS_POSTING_COLOR;
  }

  return DEFAULT_COLOR;
};
